// Shared plumbing for the ads list pages, where a checkbox toggles one id inside
// a `selected_*_ids` query param.

import { scheduleAdsTabStats } from "../ad_campaign_total_count";

export type SelectionPage = {
  // The query param carrying the selection.
  param: string;
  // Marks a page this selection belongs to; nothing syncs without it.
  root: string;
  // One selectable item, e.g. a campaign card or an ad set row.
  item: string;
  // The clickable toggle inside an item.
  toggle: string;
  // Reads an item's id out of its markup.
  idOf: (item: HTMLElement) => string | undefined;
  // Regions whose links and forms carry the selection onwards.
  containers: string[];
  // Applies the selected look to one item.
  paint: (item: HTMLElement, selected: boolean) => void;
  // Reflects the whole selection: ordering, counts, summary copy.
  reflect?: (ids: string[]) => void;
};

const bound = new Set<string>();

function readIds(param: string): string[] {
  const raw = new URL(window.location.href).searchParams.get(param);
  return raw ? raw.split(",").filter(Boolean) : [];
}

function applyParam(url: URL, param: string, ids: string[]): void {
  if (ids.length) {
    url.searchParams.set(param, ids.join(","));
  } else {
    url.searchParams.delete(param);
  }
}

// Rewrites the selection param inside a same-origin url held in an attribute,
// leaving fragment-only, `javascript:` and off-site values alone.
function rewriteUrlAttr(element: Element, attribute: string, param: string, ids: string[]): void {
  const raw = element.getAttribute(attribute);
  if (!raw || raw.startsWith("#") || /^[a-z][a-z0-9+.-]*:/i.test(raw)) return;

  const url = new URL(raw, window.location.origin);
  if (url.origin !== window.location.origin) return;

  applyParam(url, param, ids);
  element.setAttribute(attribute, `${url.pathname}${url.search}${url.hash}`);
}

function syncForm(form: HTMLFormElement, param: string, ids: string[]): void {
  const field = form.querySelector<HTMLInputElement>(`input[name="${param}"]`);
  if (field) field.value = ids.join(",");
  if ((form.getAttribute("method") || "get").toLowerCase() !== "get") {
    rewriteUrlAttr(form, "action", param, ids);
    return;
  }

  if (field || !ids.length) return;

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = param;
  input.value = ids.join(",");
  form.appendChild(input);
}

function syncItems(page: SelectionPage, ids: string[]): void {
  const selected = new Set(ids);

  document.querySelectorAll<HTMLElement>(page.item).forEach((item) => {
    const id = page.idOf(item);
    if (!id) return;

    const on = selected.has(id);
    page.paint(item, on);

    // Keep the no-JS fallback href aimed at the opposite state.
    const toggle = item.querySelector(page.toggle);
    if (toggle) {
      rewriteUrlAttr(
        toggle,
        "href",
        page.param,
        on ? ids.filter((value) => value !== id) : ids.concat(id)
      );
    }
  });
}

function syncNavigation(page: SelectionPage, ids: string[]): void {
  page.containers.forEach((selector) => {
    document.querySelectorAll(selector).forEach((container) => {
      container.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((link) => {
        if (link.closest(page.toggle)) return; // handled per item
        // Some links name a selection of their own on purpose, e.g. a row's
        // title drilling into just that one record.
        if (link.hasAttribute("data-selection-fixed")) return;
        rewriteUrlAttr(link, "href", page.param, ids);
      });

      container.querySelectorAll<HTMLFormElement>("form").forEach((form) => {
        syncForm(form, page.param, ids);
      });
    });
  });

  ["#render-async-stats", "#ads-tab-stats-async"].forEach((selector) => {
    const $source = $(selector);
    if (!$source.length) return;

    rewriteUrlAttr($source[0], "data-url", page.param, ids);
    $source.data("url", $source.attr("data-url"));
  });
}

function sync(page: SelectionPage, ids: string[]): void {
  syncItems(page, ids);
  page.reflect?.(ids);
  syncNavigation(page, ids);
}

// Records each item's server-rendered position, so a deselected item drops back
// into the slot it started in rather than being stranded at the top.
export function baselineOrder(items: HTMLElement[]): HTMLElement[] {
  items.forEach((item, index) => {
    if (item.dataset.listOrder === undefined) item.dataset.listOrder = String(index);
  });
  return items;
}

// Selected items lead the list, in the order they were picked, mirroring how the
// server pins them on the first page. Returns the items in their new order.
export function pinSelectedFirst(
  container: Element,
  items: HTMLElement[],
  ids: string[],
  idOf: (item: HTMLElement) => string | undefined
): HTMLElement[] {
  const byId = new Map(items.map((item) => [idOf(item), item]));

  const pinned = ids
    .map((id) => byId.get(id))
    .filter((item): item is HTMLElement => item !== undefined);
  const held = new Set(pinned);

  const rest = items
    .filter((item) => !held.has(item))
    .sort((a, b) => Number(a.dataset.listOrder) - Number(b.dataset.listOrder));

  const ordered = pinned.concat(rest);
  if (ordered.some((item, index) => item !== items[index])) {
    ordered.forEach((item) => container.appendChild(item));
  }

  return ordered;
}

export function initSelection(page: SelectionPage): void {
  // Delegated from the document and guarded by a module-level flag: turbo:load
  // and turbo:render both fire, and the list arrives later via render_async.
  if (!bound.has(page.param)) {
    bound.add(page.param);

    document.addEventListener("click", (event) => {
      const target = event.target as Element | null;
      const toggle = target?.closest?.(page.toggle);
      if (!toggle) return;

      const item = toggle.closest<HTMLElement>(page.item);
      const id = item ? page.idOf(item) : undefined;
      if (!id) return; // nothing to toggle locally: let the link do its job

      event.preventDefault();

      const ids = readIds(page.param);
      const next = ids.includes(id) ? ids.filter((value) => value !== id) : ids.concat(id);

      const url = new URL(window.location.href);
      applyParam(url, page.param, next);
      window.history.replaceState({}, "", url.toString());

      sync(page, next);
      // The badges count under the new selection, so they are re-read.
      scheduleAdsTabStats();
    });
  }

  if (!document.querySelector(page.root)) return;

  sync(page, readIds(page.param));
}
